import { Box, CircularProgress } from '@mui/material';
import { Document, Page, PDFViewer, Text, View } from '@react-pdf/renderer';
import { DateTime } from 'luxon';
import { Html } from 'react-pdf-html';
import { DocumentType } from 'shared/documents/DocumentGenerator';
import useDocumentPreview from '../hooks/useDocumentPreview';

export const DOCUMENT_SIZES = {
  small: { width: '420', height: '540' },
  medium: { width: '560', height: '720' },
  large: { width: '800', height: '1000' },
};

export const renderPages = (pages, settings = {}) => {
  return pages?.map((page, i) => {
    return (
      <Page
        size="LETTER"
        dpi={72}
        key={i}
        wrap
        style={{
          paddingBottom: settings.footerMargin ?? '20px',
          minHeight: '726px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          fontFamily: 'Montserrat',
          paddingLeft: settings.paddingLeft,
          paddingRight: settings.paddingRight,
        }}
      >
        {settings.headerMargin && (
          <View
            fixed
            wrap
            style={{
              marginTop: '-8px', // remove gap
            }}
            render={({ pageNumber }) => {
              if (pageNumber === 1 && settings.hideFirstHeaderMargin) return null;

              return <View style={{ height: settings.headerMargin }} />;
            }}
          />
        )}
        <Html
          renderers={{
            body: ({ children }) => {
              return children;
            },
            section: ({ children, style, element }) => {
              const isBody = element.classList.contains('pageBody');
              // keep-together is a div-level contract: honouring it on a <section> would make the
              // whole container atomic, and a container taller than one page is clipped, not moved.
              // TODO(TAS-3869): a mis-placed keep-together is silent, as is `fixed` + keep-together
              // (shouldBreak() early-returns on any `fixed` key). Needs a warning channel first.
              const holdsKeepTogether = Boolean(element.querySelector('div.keep-together'));

              return (
                <View
                  style={
                    // override the styles so we have more control over them after being rendered.
                    isBody
                      ? {
                          ...style,
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          paddingTop: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          flexGrow: 1,
                          gap: '8px',
                          height: 'auto',
                        }
                      : {
                          ...style,
                          // A definite height is stamped onto the split section and crushes the block it moved.
                          ...(holdsKeepTogether ? { height: 'auto', minHeight: '100%' } : { height: '100%' }),
                          display: 'flex',
                          flexDirection: 'column',
                          fontFamily: 'Montserrat',
                        }
                  }
                  wrap
                >
                  {children}
                </View>
              );
            },
            // span is used to ensure that text is rendered correctly (with alignment styles)
            span: ({ children, style }) => {
              return (
                <Text style={style} wrap={false}>
                  {children}
                </Text>
              );
            },

            div: ({ children, style, element }) => {
              const fixed = element.classList.contains('fixed');
              const hasWrap = element.classList.contains('wrap');
              const isTable = element.classList.contains('flex-table');
              const isColumn = element.classList.contains('flex-column');
              const keepTogether = element.classList.contains('keep-together');
              const canWrap = (hasWrap || isTable || isColumn) && !keepTogether;

              // `fixed` must be ABSENT, not false: shouldBreak() tests it by presence.
              const fixedProp = fixed ? { fixed: true } : keepTogether ? {} : { fixed: false };

              return (
                <View style={style} wrap={canWrap} {...fixedProp}>
                  {children}
                </View>
              );
            },

            pagenumber: ({ style }) => {
              return <Text style={style} render={({ pageNumber }) => pageNumber} />;
            },
            totalpages: ({ style }) => {
              return <Text style={style} render={({ totalPages }) => totalPages} />;
            },
          }}
        >
          {page}
        </Html>
        {!settings?.hideFooter && (
          <View
            style={{
              position: 'absolute',
              bottom: 10 + (settings.footerMargin ? parseFloat(settings.footerMargin) : 0),
              left: 0,
              right: 0,
              height: 15,
              marginBottom: '-6px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: '24px',
              paddingTop: 1,
            }}
            fixed
          >
            <View style={{ flexGrow: 1, display: 'flex', flexDirection: 'row' }}>
              <Text style={{ fontSize: 6, letterSpacing: '1px', color: '#474746' }}>
                {settings?.footerText ?? 'POWERED BY AGRIFUL'}
              </Text>
            </View>
            <Text style={{ marginRight: 10, color: '#474746', fontSize: 7 }}>
              {DateTime.now().toFormat(`MM/dd/yyyy 'at' HH:mm a`)}
            </Text>
            <Text
              render={({ pageNumber, totalPages }) => `${pageNumber} of ${totalPages}`}
              fixed
              style={{ color: '#474746', fontSize: 7 }}
            />
          </View>
        )}
      </Page>
    );
  });
};

export default function DocumentPreview({
  width,
  height,
  data,
  documentTemplate,
  entityType,
  type,
  loading,
  extendedData,
}: {
  width: string;
  height: string;
  data: unknown;
  documentTemplate?: DocumentType;
  type: string;
  loading: boolean;
  extendedData?: Record<string | number, unknown>;
}) {
  const pages = useDocumentPreview({ data, documentTemplate, entityType, type, extendedData });

  if (!pages) {
    return null;
  }

  if (loading) {
    return (
      // place in center
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height, width: width }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PDFViewer width={width} height={height} showToolbar={false} style={{ border: 'none', borderRadius: '4px' }}>
      <Document>{renderPages(pages)}</Document>
    </PDFViewer>
  );
}
